"use client";

import { useEffect, useState } from "react";
import { fetchGlobalDefaults, updateGlobalDefault } from "@/lib/queries";
import { shiftDecimal } from "@/lib/format";
import type { GlobalDefault } from "@/lib/types";

const UNIT_SUFFIX: Record<GlobalDefault["unit"], string> = {
  percent: "%",
  dollars: "$",
  years: "yr",
  multiplier: "×",
  count: "",
};

// Body-only (no Header/page wrapper) so it composes into the Settings
// sidebar shell. Logic moved here unchanged from the old standalone
// /settings page.
export function DefaultsSection() {
  const [defaults, setDefaults] = useState<GlobalDefault[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchGlobalDefaults()
      .then((rows) => {
        setDefaults(rows);
        const initial: Record<string, string> = {};
        for (const row of rows) {
          if (row.unit === "percent") {
            // numeric_value is stored as a fraction (0.07 for 7%). Shift
            // the decimal point two places right as a string op -- never
            // multiply by 100 as a float, which can't represent most
            // decimals exactly and drifts (0.07 * 100 = 7.000000000000001).
            initial[row.key] = shiftDecimal(String(row.numeric_value), 2);
          } else {
            initial[row.key] = String(row.numeric_value);
          }
        }
        setDrafts(initial);
      })
      .catch((e) => setError(e.message ?? String(e)));
  }, []);

  const handleSave = async (row: GlobalDefault) => {
    const draft = drafts[row.key];
    if (draft.trim() === "" || Number.isNaN(Number(draft))) return;
    // Shift the decimal point left two places as a string op (exact),
    // then parse once at the end -- avoids `parsed / 100` drifting a
    // value that was already exact as typed (e.g. 7.87535).
    const storedValue = row.unit === "percent" ? Number(shiftDecimal(draft, -2)) : Number(draft);

    setSaving(row.key);
    try {
      await updateGlobalDefault(row.key, storedValue);
      setDefaults((prev) =>
        prev ? prev.map((r) => (r.key === row.key ? { ...r, numeric_value: storedValue } : r)) : prev
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold text-[var(--color-text-primary)]">Global Defaults</h1>
      <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
        These values drive how listings are seeded and how projections are computed. Changes here
        take effect automatically on the next daily pipeline run — every Default Offer is reseeded
        from current defaults, then projections are recomputed.
      </p>

      {error && (
        <div
          className="mt-4 rounded-md border px-4 py-3 text-sm"
          style={{ borderColor: "var(--color-negative)", backgroundColor: "var(--color-negative-muted)", color: "var(--color-negative)" }}
        >
          {error}
        </div>
      )}

      {!defaults && !error && (
        <div className="py-16 text-center text-sm text-[var(--color-text-secondary)]">Loading…</div>
      )}

      {defaults && (
        <div className="mt-6 space-y-3">
          {defaults.map((row) => (
            <div
              key={row.key}
              className="flex items-center justify-between gap-4 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-1)] px-4 py-3"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-[var(--color-text-primary)]">{row.label}</div>
                {row.description && (
                  <div className="mt-0.5 text-xs text-[var(--color-text-tertiary)]">{row.description}</div>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="any"
                    value={drafts[row.key] ?? ""}
                    onChange={(e) => setDrafts((prev) => ({ ...prev, [row.key]: e.target.value }))}
                    className="w-24 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-0)] px-2 py-1.5 text-right text-sm tabular-nums text-[var(--color-text-primary)] focus:border-[var(--color-accent)]"
                  />
                  <span className="text-xs text-[var(--color-text-tertiary)]">{UNIT_SUFFIX[row.unit]}</span>
                </div>
                <button
                  onClick={() => handleSave(row)}
                  disabled={saving === row.key}
                  className="rounded-md border px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                  style={{ borderColor: "var(--color-accent)", color: "var(--color-accent-strong)" }}
                >
                  {saving === row.key ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
